import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ForcePasswordChange from './pages/ForcePasswordChange';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Pipeline from './pages/Pipeline';
import Deals from './pages/Deals';
import DealPage from './pages/DealPage';
import PipelineDeal from './pages/PipelineDeal';
import Underwriting from './pages/Underwriting';
import BuyerMatch from './pages/BuyerMatch';
import DealPackets from './pages/DealPackets';
import Buyers from './pages/Buyers';
import BuyerDetail from './pages/BuyerDetail';
import MlsOpportunities from './pages/MlsOpportunities';
import ImportDeals from './pages/ImportDeals';
import AddDeal from './pages/AddDeal';
import OffMarket from './pages/OffMarket';
import Communications from './pages/Communications';
import Settings from './pages/Settings';
import TemplateBuilder from './pages/TemplateBuilder';
import Admin from './pages/Admin';
import Lists from './pages/Lists';
import { ListsProvider } from './components/Lists';
import MySales from './pages/MySales';
import { canAcquire } from './utils/roles';

// Acquisitions pages. A sales agent who follows a stale link or bookmark lands on the Pipeline rather
// than a page full of 403s; the API refuses them either way (backend/lib/roles.js).
function AcqOnly({ children }) {
  const { role } = useAuth();
  return canAcquire(role) ? children : <Navigate to="/pipeline" replace />;
}

// Full-screen loader while we bootstrap auth state from GET /api/auth/me.
function BootLoader() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-500)', fontSize: 14 }}>
      Loading…
    </div>
  );
}

// Authenticated app shell — wires logout (API call + redirect) into the existing Layout.
function ProtectedShell() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const onLogout = async () => { await logout(); navigate('/login'); };
  return (
    <ListsProvider>
      <Layout onLogout={onLogout} />
    </ListsProvider>
  );
}

function AppRoutes() {
  const { loading, user } = useAuth();
  if (loading) return <BootLoader />;

  // A member created with a temporary password (scripts/add-org-user.js) must choose their own
  // before anything else. Rendering the screen INSTEAD of the router — rather than adding a route
  // and redirecting to it — means there is no path around it: no sidebar, no deep link, no
  // back-button. The flag clears server-side in POST /api/auth/change-password.
  if (user && user.must_change_password) return <ForcePasswordChange />;

  return (
    <Routes>
      {/* Public auth routes — redirect into the app if already signed in */}
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/signup" element={user ? <Navigate to="/" replace /> : <Signup />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/" replace /> : <ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {user ? (
        <Route element={<ProtectedShell />}>
          {/* `/` is the personal Dashboard — the page an agent lands on at sign-in. The org-wide
              Pipeline moved to /pipeline; /pipeline/deal/:id is a different, older route and is
              matched separately. */}
          <Route path="/" element={<Dashboard />} />
          <Route path="/pipeline" element={<Pipeline />} />
          <Route path="/deals" element={<AcqOnly><Deals /></AcqOnly>} />
          <Route path="/pipeline/deal/:id" element={<PipelineDeal />} />
          <Route path="/my-sales" element={<MySales />} />
          <Route path="/mls-opportunities" element={<AcqOnly><MlsOpportunities /></AcqOnly>} />
          <Route path="/import" element={<AcqOnly><ImportDeals /></AcqOnly>} />
          <Route path="/deals/new" element={<AcqOnly><AddDeal /></AcqOnly>} />
          <Route path="/deals/:id" element={<DealPage />} />
          <Route path="/off-market" element={<AcqOnly><OffMarket /></AcqOnly>} />
          <Route path="/underwriting/:id" element={<Underwriting />} />
          <Route path="/buyer-match/:id" element={<BuyerMatch />} />
          <Route path="/packets/:id" element={<DealPackets />} />
          <Route path="/buyers" element={<Buyers />} />
          <Route path="/buyers/:id" element={<BuyerDetail />} />
          <Route path="/communications" element={<Communications />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/templates/:slot" element={<TemplateBuilder />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/lists" element={<Lists />} />
          <Route path="/lists/:id" element={<Lists />} />
        </Route>
      ) : (
        // Not signed in → any protected path redirects to login.
        <Route path="*" element={<Navigate to="/login" replace />} />
      )}

      <Route path="*" element={<Navigate to={user ? '/' : '/login'} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
